import { action, computed, observable, toJS } from 'mobx';
import { DEFAULT_TASKS_PER_PAGE_NUMBER } from '~/services/tasks.service';
import {
    ITaskSort,
    TaskFilters,
    TaskSortBy
} from '~/components/Tasks/types/tasks.types';
import { getFiltersCount } from '~/components/Tasks/tasks.utils';
import { RootStore } from '~/stores/root.store';
import { TTasksAdvancedFilters } from '~/apps/main/Workspace/TasksTab/workspace-tasks.types';
import { BaseTasksStore } from './base-tasks.store';

export const DEFAULT_ADVANCED_FILTERS: TTasksAdvancedFilters = {
    due_date: null,
    status: 'open',
    type: 'all',
    task_groups: new Set<string>(),
    assignees: new Set<string>()
};

export class TasksStore extends BaseTasksStore {
    rootStore: RootStore;
    @observable workspaceId?: string;
    @observable tasksPerPageNumber: number = DEFAULT_TASKS_PER_PAGE_NUMBER;
    @observable currentPage: number = 0;
    @observable nextOffsets: {
        tasks_misc: number;
        wf_approvals_misc: number;
        wf_approvals_after_step_execution: number;
    };
    @observable isLoading = true;
    @observable selectedFilter: TaskFilters;
    @observable advancedFilters: TTasksAdvancedFilters =
        DEFAULT_ADVANCED_FILTERS;
    @observable selectedSort: ITaskSort = {
        sortBy: TaskSortBy.DUE_DATE,
        sortDesc: false
    };
    @observable taskIdInEdit: string = null;
    @observable taskInEditAnalyticsOrigin: string = null;
    @observable isAddingNewTask = false;
    @observable taskIdForCleanup: string | null = null;

    constructor(rootStore: RootStore) {
        super();
        this.rootStore = rootStore;
        this.resetTasks();
    }

    @action.bound
    selectWorkspace(id: string) {
        this.workspaceId = id;
    }

    @action.bound
    setTaskIdInEdit(taskId: string) {
        this.taskIdInEdit = taskId;
    }

    @action.bound
    setTaskInEditAnalyticsOrigin(taskId: string) {
        this.taskInEditAnalyticsOrigin = taskId;
    }

    @action.bound
    setIsLoading(isLoadingNow: boolean) {
        this.isLoading = isLoadingNow;
    }

    @action.bound
    setNextPageOffsets(newNextOffsets) {
        this.nextOffsets = newNextOffsets;
    }

    @action.bound
    resetTasks() {
        this.resetPageStatus();
        this.setTaskList([]);
        this.setHasLoadedTasks(false);
        this.setTaskGroups([]);
        this.setSelectedFilter(TaskFilters.ALL);
        this.setSelectedSort({
            sortBy: TaskSortBy.DUE_DATE,
            sortDesc: false
        });
        this.resetAdvancedFilters();
        this.setTaskCount({
            all: 0,
            approvals: 0,
            completed: 0,
            overdue: 0,
            tasks: 0,
            this_week: 0,
            today: 0,
            total: 0
        });
        this.clearTaskCleanup();
        this.workspaceId = null;
    }

    @action.bound
    resetPageStatus() {
        this.setNextPageOffsets({
            tasks_misc: 0,
            wf_approvals_misc: 0,
            wf_approvals_after_step_execution: 0
        });
        this.setCurrentPage(0);
    }

    @action.bound
    setTasksPerPageNumber(newTasksPerPageNumber) {
        this.tasksPerPageNumber = newTasksPerPageNumber;
    }

    @action.bound
    setCurrentPage(newPage) {
        this.currentPage = newPage;
    }

    @action.bound
    setSelectedFilter(newFilter) {
        this.selectedFilter = newFilter;
    }

    @action.bound
    setAdvancedFilters(filters: TTasksAdvancedFilters) {
        this.advancedFilters = filters;
    }

    @action.bound
    resetAdvancedFilters() {
        this.advancedFilters = DEFAULT_ADVANCED_FILTERS;
    }

    @action.bound
    resetFilters() {
        if (this.workspaceId && this.advancedFiltersCount > 0) {
            this.resetAdvancedFilters();
        } else {
            this.setSelectedFilter(TaskFilters.ALL);
        }
    }

    @action.bound
    dismissReminder(taskId: string) {
        const newTaskList = this.taskList.map(task =>
            task._id === taskId ? { ...task, reminder: null } : task
        );
        this.setTaskList(newTaskList);
    }

    @action.bound
    setSelectedSort(newSort: ITaskSort) {
        this.selectedSort = newSort;
    }

    @action.bound
    setIsAddingNewTask(isAddingNewTask: boolean) {
        this.isAddingNewTask = isAddingNewTask;
    }

    @computed
    get hasMoreTasksToShow() {
        return (
            this.taskList.length >=
            this.tasksPerPageNumber * (this.currentPage + 1)
        );
    }

    @computed
    get advancedFiltersCount() {
        return getFiltersCount(toJS(this.advancedFilters));
    }

    @computed
    get isFilterActive() {
        return this.workspaceId
            ? this.advancedFiltersCount > 0
            : this.selectedFilter !== TaskFilters.ALL;
    }

    @computed
    get existingGroupsColors() {
        return this.taskGroups.map(group => group.color);
    }

    @action.bound
    setTaskForCleanup(taskId: string | null) {
        this.taskIdForCleanup = taskId;
    }

    @action.bound
    clearTaskCleanup() {
        this.taskIdForCleanup = null;
    }
}
